import { useEffect, useState } from "react";
import { X, Warehouse, Plus, Trash2, Send, PackageCheck } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS_STYLE = {
  magazyn: "bg-blue-500/15 text-blue-300 border-blue-500/50",
  u_montera: "bg-amber-500/15 text-amber-300 border-amber-500/50",
  zainstalowane: "bg-green-500/15 text-green-300 border-green-500/50",
};

export default function WarehousePanel({ role, onClose }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [monters, setMonters] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ category: "gniazdko", name: "", serial: "", qty: 1 });

  const load = () => api.get("/warehouse", { params: filter ? { location: filter } : {} }).then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    api.get("/warehouse/categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/warehouse/monters").then((r) => setMonters(r.data || [])).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const add = async () => {
    if (!form.name) { toast.error("Podaj nazwę"); return; }
    try { await api.post("/warehouse", form); toast.success("Przyjęto na stan"); setForm({ category: form.category, name: "", serial: "", qty: 1 }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const issue = async (it) => {
    const to = window.prompt("ID montera? Wpisz nazwę z listy:\n" + monters.map((m) => `${m.name} = ${m.id}`).join("\n"));
    if (!to) return;
    const qty = it.serial ? 1 : Number(window.prompt("Ilość do wydania:", "1") || 1);
    try { await api.post(`/warehouse/${it.id}/issue`, { to_user: to, qty }); toast.success("Wydano monterowi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const consume = async (it) => {
    const cid = window.prompt("ID klienta (montaż)? Lista:\n" + clients.slice(0, 40).map((c) => `${c.name} = ${c.id}`).join("\n"));
    if (!cid) return;
    const qty = it.serial ? 1 : Number(window.prompt("Ilość zużyta:", "1") || 1);
    try { await api.post("/warehouse/consume", { item_id: it.id, qty, client_id: cid }); toast.success("Zużyto — zdjęto ze stanu"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (it) => { if (!window.confirm("Usunąć pozycję?")) return; try { await api.delete(`/warehouse/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="warehouse-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2 font-heading font-bold text-white"><Warehouse size={18} className="text-cyan-400" /> {t("Magazyn")}</div>
        <button onClick={onClose} data-testid="warehouse-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>

      {canEdit && (
        <div className="px-4 py-3 border-b border-slate-800 grid grid-cols-12 gap-2 items-end">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="wh-cat" className="col-span-3 bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white">
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("Nazwa/model")} data-testid="wh-name" className="col-span-4 bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
          <input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} placeholder={t("Nr seryjny (opc.)")} data-testid="wh-serial" className="col-span-3 bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
          <input type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} disabled={!!form.serial} data-testid="wh-qty" className="col-span-1 bg-slate-950 border border-slate-700 rounded px-1 py-2 text-sm text-white disabled:opacity-50" />
          <button onClick={add} data-testid="wh-add" className="col-span-1 bg-cyan-600 hover:bg-cyan-500 text-white rounded py-2 flex items-center justify-center"><Plus size={16} /></button>
        </div>
      )}

      <div className="px-4 py-2 border-b border-slate-800 flex gap-2 items-center">
        <span className="text-xs text-slate-500">{t("Lokalizacja:")}</span>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="wh-filter" className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
          <option value="">{t("Wszystkie")}</option>
          <option value="magazyn">{t("Magazyn główny")}</option>
          {monters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {rows.length === 0 ? <div className="text-sm text-slate-500 text-center py-10">{t("Brak pozycji na stanie.")}</div> : rows.map((it) => (
          <div key={it.id} className="rounded-lg border border-slate-700 bg-slate-800/40 p-3" data-testid={`wh-item-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm text-white"><span className="text-[10px] uppercase text-slate-500 mr-1">{it.category}</span>{it.name} {it.serial && <span className="font-mono text-[11px] text-slate-400">· SN {it.serial}</span>}</div>
                <div className="text-xs text-slate-400 mt-0.5">Ilość: <b className="font-mono">{it.qty}</b> · {it.location_name}{it.client_name ? ` → klient: ${it.client_name}` : ""}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_STYLE[it.status] || ""}`}>{it.status}</span>
            </div>
            {canEdit && (
              <div className="flex gap-2 mt-2">
                {it.status === "magazyn" && <button onClick={() => issue(it)} data-testid={`wh-issue-${it.id}`} className="text-xs bg-amber-600/80 hover:bg-amber-500 text-white rounded px-3 py-1.5 flex items-center gap-1"><Send size={13} /> {t("Wydaj monterowi")}</button>}
                {it.status === "u_montera" && <button onClick={() => consume(it)} data-testid={`wh-consume-${it.id}`} className="text-xs bg-green-600 hover:bg-green-500 text-white rounded px-3 py-1.5 flex items-center gap-1"><PackageCheck size={13} /> {t("Zużyj u klienta")}</button>}
                {role === "admin" && it.status !== "zainstalowane" && <button onClick={() => del(it)} data-testid={`wh-del-${it.id}`} className="text-xs bg-slate-800 hover:bg-red-900/40 text-red-300 rounded px-3 py-1.5"><Trash2 size={13} /></button>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
